import { environment } from '@environments/environment';
import { SalesProjectService } from '@http/clients/reporting-app/sales-projects.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import {
    formatCurrencyCompact,
    formatNumbersCompact,
    percentageFormatter,
    truncateValue
} from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import { GridTooltipComponent } from '../report-single-tabs/grid-tab/tooltip/grid-tooltip/grid-tooltip.component';
import {
    marketsEditorConfig,
    opportunityCompetingBidderEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const SalesProjects: IReportConfiguration<SalesProjectService> = {
    base: {
        type: ReportType.SalesProjects,
        serviceType: SalesProjectService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getSalesEstimatesBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getSalesEstimatesMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'accepted', label: 'Accepted' },
                        { name: 'rejected', label: 'Rejected' },
                        { name: 'canceled', label: 'Canceled' },
                        { name: 'onHold', label: 'On-hold' }
                    ],
                    rows: [
                        { name: 'Qty', label: 'Quantity' },
                        { name: 'Proportion', label: 'Percentage', format: 'percent' }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Projects by Sell Price'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'projectName',
                            yKey: 'sellPrice',
                            yName: 'Sell Price '
                        },
                        {
                            type: 'line',
                            xKey: 'projectName',
                            yKey: 'grossMargin',
                            yName: 'Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['sellPrice'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['grossMargin'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'opportunityValue',
                            yName: 'Opportunity Value'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'bidAmount',
                            yName: 'Bid Amount'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: ['opportunityValue', 'bidAmount', 'awardedAmount'],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getSalesEstimatesSummaryValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'projectId',
                        headerName: 'BID',
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (!params.value) {
                                return;
                            }
                            if (params.data?.projectArchived) {
                                return params.value;
                            } else {
                                const link = `${environment.SalesAppClientBaseUrl}/${environment.SalesAppClientProjectOverviewUrl}/${params.value}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                        }
                    },
                    { field: 'projectName', headerName: 'Bid Name', enableRowGroup: true },
                    { field: 'projectDueDate', headerName: 'Project Due Date', type: 'date' },
                    { field: 'status', headerName: 'Project Status', enableRowGroup: true },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'salesPerson', headerName: 'Salesperson', enableRowGroup: true },
                    { field: 'sellPrice', headerName: 'Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'grossMargin', headerName: 'Gross Margin (%)', type: 'customPercentage', aggFunc: 'avg' },
                    { field: 'totalBuildingSqFt', headerName: 'Total Building Sq. Ft.' },
                    { field: 'pricePerSqFt', headerName: 'Sell Price / Sq. Ft.' },
                    { field: 'markets', headerName: 'Market' },
                    { field: 'lead', headerName: 'Opportunity Source', enableRowGroup: true },
                    { field: 'competingBidders', headerName: 'Competing Bidder', enableRowGroup: true },
                    { field: 'division', headerName: 'Department', enableRowGroup: true },
                    { field: 'customer', headerName: 'Customer', enableRowGroup: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    { field: 'potentialValue', headerName: 'Potential Value ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'probability', headerName: 'Probability', type: 'percentageString', aggFunc: 'avg' },
                    { field: 'speculativeValue', headerName: 'Speculative Value', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'customFieldValues',
                        headerName: 'Custom Fields',
                        tooltipField: 'customFieldValues',
                        tooltipComponent: GridTooltipComponent
                    },
                    {
                        field: 'opportunityLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'opportunityLastUpdatedNote'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            opportunityCompetingBidderEditorConfig
        ]
    }
};
